import { Container } from '@chakra-ui/react';
import { useVerifyShareLinkMutation } from '@client/graphql/__generated__/main-operations';
import { PublicUserFragment } from '@client/graphql/__generated__/types';
import { PropsWithChildren, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { ExpiredLinkCard } from '../../components/BuyerOfferAnalysis/BuyerView/ExpiredLinkCard';
import { useClientUpdateNotificationContext } from '../../components/ClientUpdateNotification/ClientUpdateNotificationContext';
import { SharedPageContext } from '../../contexts/SharedPageContext';

interface SharedPageProps extends PropsWithChildren {
  // When omitted, the expired-link state shows a contact message instead of a
  // resend button (e.g. system-generated links with no recipient to resend to).
  resendLink?: (token: string) => Promise<void>;
  isResendingLink?: boolean;
  // Optional verify-binding fields, passed by the page so SharedPage stays
  // route-agnostic. Add future per-share-type binding fields here as more
  // share pages reuse SharedPage.
  ingestionFolderUuid?: string;
}

export const SharedPage = ({
  children,
  resendLink,
  isResendingLink = false,
  ingestionFolderUuid,
}: SharedPageProps) => {
  const { token: tokenParam } = useParams();
  const [searchParams] = useSearchParams();
  // The token may arrive as a path param (BOA / listing share links) or as a
  // `token` query param (agent compliance review links).
  const token = tokenParam ?? searchParams.get('token') ?? undefined;
  const [isExpired, setIsExpired] = useState(false);
  const [isShareLinkVerified, setIsShareLinkVerified] = useState(false);
  const [isForPdf, setIsForPdf] = useState(false);
  const [sharedBy, setSharedBy] = useState<PublicUserFragment | undefined>(
    undefined
  );
  const navigate = useNavigate();
  const { setIsPuppeteer } = useClientUpdateNotificationContext();

  const [verifyShareLink, { loading }] = useVerifyShareLinkMutation({
    variables: {
      token: token || '',
      // Present only for agent compliance review links; binds verification to
      // the folder so a token for a different folder fails fast here.
      ingestionFolderUuid,
    },
  });

  useEffect(() => {
    if (!token) {
      navigate('/');
    }

    void (async () => {
      const { data } = await verifyShareLink({});
      const verification = data?.verifyShareLink;

      const isExpiredForPdf = verification?.isForPdf && verification?.isExpired;

      if (!verification || !verification.isValid || isExpiredForPdf) {
        navigate('/404');
      }

      if (verification?.isForPdf) {
        setIsForPdf(true);
        setIsPuppeteer(true);
      }

      if (verification?.isExpired) {
        setIsExpired(true);
      } else if (verification?.isValid) {
        // Only let children load token-bound data once the link is confirmed
        // valid and unexpired.
        setIsShareLinkVerified(true);
      }

      setSharedBy(verification?.sharedBy);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <CenterSpinner />;
  }

  if (isExpired) {
    return (
      <Container centerContent>
        <ExpiredLinkCard
          loading={isResendingLink}
          resendLink={resendLink}
          token={token || ''}
        />
      </Container>
    );
  }

  return (
    <SharedPageContext.Provider
      value={{ isForPdf, sharedBy, isShareLinkVerified }}
    >
      {children}
    </SharedPageContext.Provider>
  );
};
